import { CaretLeft as ChevronLeft, CaretRight as ChevronRight } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { useTranslationV2 } from "@/hooks/useTranslationV2";
import { useRouterState } from "@tanstack/react-router";
import type { MouseEvent } from "react";

interface ListingsPaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  totalCount?: number;
  itemsPerPage?: number;
}

export const ListingsPagination = ({
  currentPage,
  totalPages,
  onPageChange,
  totalCount,
  itemsPerPage = 10,
}: ListingsPaginationProps) => {
  const { t } = useTranslationV2('vehicles');
  // Real <a href> links so crawlers (and no-JS visitors) can follow pagination.
  const { pathname, searchStr } = useRouterState({
    select: (s) => ({ pathname: s.location.pathname, searchStr: s.location.searchStr }),
  });
  const hrefFor = (page: number) => {
    const params = new URLSearchParams(searchStr);
    if (page <= 1) params.delete("page");
    else params.set("page", String(page));
    const qs = params.toString();
    return `${pathname}${qs ? `?${qs}` : ""}`;
  };
  const go = (page: number) => (e: MouseEvent<HTMLAnchorElement>) => {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
    e.preventDefault();
    onPageChange(page);
  };
  
  if (totalPages <= 1) return null;

  // Generate page numbers to display
  const getVisiblePages = () => {
    const pages: (number | 'ellipsis')[] = [];
    const maxVisible = 5;
    
    if (totalPages <= maxVisible + 2) {
      // Show all pages if total is small
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);
      
      if (currentPage > 3) {
        pages.push('ellipsis');
      }
      
      // Show pages around current
      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);
      
      for (let i = start; i <= end; i++) {
        if (!pages.includes(i)) {
          pages.push(i);
        }
      }
      
      if (currentPage < totalPages - 2) {
        pages.push('ellipsis');
      }
      
      // Always show last page
      if (!pages.includes(totalPages)) {
        pages.push(totalPages);
      }
    }
    
    return pages;
  };

  const visiblePages = getVisiblePages();
  
  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalCount || 0);

  return (
    <div className="flex flex-col items-center gap-4 py-12">
      {/* Results info */}
      {totalCount && (
        <p className="text-xs uppercase tracking-wide text-muted-foreground font-medium">
          {startItem}-{endItem} din {totalCount} rezultate
        </p>
      )}

      {/* Pagination controls */}
      <nav className="flex w-full items-center justify-center gap-1 overflow-hidden sm:w-auto sm:gap-2" role="navigation" aria-label="Pagination">
        {/* Previous button — text only, primary color */}
        {currentPage > 1 ? (
          <a
            href={hrefFor(currentPage - 1)}
            onClick={go(currentPage - 1)}
            rel="prev"
            aria-label="Pagina anterioară"
            className="inline-flex h-10 w-10 shrink-0 items-center justify-center gap-1 text-sm font-semibold text-primary transition-colors hover:text-primary/80 sm:w-auto sm:px-3"
          >
            <ChevronLeft className="h-4 w-4" />
            <span className="hidden sm:inline">Înapoi</span>
          </a>
        ) : (
          <span aria-disabled="true" className="inline-flex h-10 w-10 shrink-0 cursor-not-allowed items-center justify-center gap-1 text-sm font-semibold text-muted-foreground sm:w-auto sm:px-3">
            <ChevronLeft className="h-4 w-4" />
            <span className="hidden sm:inline">Înapoi</span>
          </span>
        )}

        {/* Page numbers */}
        <div className="flex min-w-0 items-center gap-1">
          {visiblePages.map((page, index) => {
            if (page === 'ellipsis') {
              return (
                <span
                  key={`ellipsis-${index}`}
                  className="hidden h-10 w-10 items-center justify-center text-muted-foreground sm:flex"
                >
                  …
                </span>
              );
            }

            const isActive = page === currentPage;
            return (
              <a
                key={page}
                href={hrefFor(page)}
                onClick={go(page)}
                aria-label={`Pagina ${page}`}
                aria-current={isActive ? "page" : undefined}
                className={
                  isActive
                    ? "inline-flex items-center justify-center w-10 h-10 rounded-lg bg-primary text-primary-foreground font-serif text-base shadow-xs transition-all"
                    : "inline-flex items-center justify-center w-10 h-10 rounded-lg bg-transparent text-muted-foreground text-sm font-medium hover:bg-primary/10 hover:text-primary transition-colors"
                }
              >
                {page}
              </a>
            );
          })}
        </div>

        {/* Next button — text only, primary color */}
        {currentPage < totalPages ? (
          <a
            href={hrefFor(currentPage + 1)}
            onClick={go(currentPage + 1)}
            rel="next"
            aria-label="Pagina următoare"
            className="inline-flex h-10 w-10 shrink-0 items-center justify-center gap-1 text-sm font-semibold text-primary transition-colors hover:text-primary/80 sm:w-auto sm:px-3"
          >
            <span className="hidden sm:inline">Următor</span>
            <ChevronRight className="h-4 w-4" />
          </a>
        ) : (
          <span aria-disabled="true" className="inline-flex h-10 w-10 shrink-0 cursor-not-allowed items-center justify-center gap-1 text-sm font-semibold text-muted-foreground sm:w-auto sm:px-3">
            <span className="hidden sm:inline">Următor</span>
            <ChevronRight className="h-4 w-4" />
          </span>
        )}
      </nav>
    </div>
  );
};
